import React, { useState } from 'react';
import { Flight } from '../../../types';
import { addFlight, fetchFlights, updateFlight } from '../../../features/admin/flightSlice';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
type Props = {
    initialData: Flight | null;
    onClose: () => void;
    onSuccess: () => void;
};

const FlightModal = ({ initialData, onClose, onSuccess }: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>()
    const [formData, setFormData] = useState<Flight>({
        id: initialData?.id ?? 0,
        createAt: initialData?.createAt ?? new Date().toISOString(),
        tripTicket: initialData?.tripTicket || '',
        companyImage: initialData?.companyImage || '',
        arriveTime: initialData?.arriveTime || '',
        departureTime: initialData?.departureTime || '',
        date: initialData?.date || '',
        inTrip: initialData?.inTrip || false,
        price: initialData?.price || 0,
        classType: initialData?.classType || '',
        type: initialData?.type || '',
        fromCountry: initialData?.fromCountry || '',
        toCountry: initialData?.toCountry || '',
        currencyCode: initialData?.currencyCode || '',
    });
    const [companyImageFile, setCompanyImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string>(
        typeof formData.companyImage === 'string' ? formData.companyImage : ''
    );
    const inputStyle = "w-full px-3 py-1 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;

        if (type === 'checkbox') {
            const checked = (e.target as HTMLInputElement).checked;
            setFormData(prev => ({
                ...prev,
                [name]: checked
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };


    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setCompanyImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async () => {
        if (!formData.tripTicket || !formData.fromCountry || !formData.toCountry) {
            toast.error(t('add-trip-error'));
            return;
        }

        try {
            const data = new FormData();
            data.append("tripTicket", formData.tripTicket);
            data.append("arriveTime", formData.arriveTime);
            data.append("departureTime", formData.departureTime);
            data.append("date", formData.date);
            data.append("inTrip", String(formData.inTrip));
            data.append("price", formData.price.toString());
            data.append("classType", formData.classType);
            data.append("type", formData.type);
            data.append("fromCountry", formData.fromCountry);
            data.append("toCountry", formData.toCountry);
            data.append("currencyCode", formData.currencyCode);

            if (companyImageFile) {
                data.append("companyImage", companyImageFile);
            }

            if (initialData?.id) {
                data.append("id", initialData.id.toString());
                await dispatch(updateFlight({ id: initialData?.id, data }));
            } else {
                await dispatch(addFlight(data));
            }

            await dispatch(fetchFlights());
            onSuccess();
            onClose();
        } catch (error) {
        }
    };



    return (
        <div className="fixed inset-0 bg-black p-5 bg-opacity-30 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-xl w-full max-w-xl shadow-lg space-y-4 overflow-y-auto max-h-[90vh]">
                <h2 className="text-xl font-bold text-center">
                    {initialData ? t("trip-title-edit") : t("trip-title-create")}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">{t("trip-ticket")}</label>
                        <input name="tripTicket" value={formData.tripTicket} onChange={handleChange} className={`${inputStyle}`} />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">{t("price")}</label>
                        <input type="number" name="price" value={formData.price} onChange={handleChange} className={`${inputStyle}`} />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">{t("from")}</label>
                        <input name="fromCountry" value={formData.fromCountry} onChange={handleChange} className={`${inputStyle}`} />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">{t("to")}</label>
                        <input name="toCountry" value={formData.toCountry} onChange={handleChange} className={`${inputStyle}`} />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">{t("trip-date")}</label>
                        <DatePicker
                            selected={formData.date ? new Date(formData.date) : null}
                            onChange={(date) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    date: date?.toISOString() || "",
                                }))
                            }
                            timeFormat="dd-MM-yyyy"
                            dateFormat="dd-MM-yyyy"
                            className={`${inputStyle}`}
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">{t("departure")}</label>
                        <DatePicker
                            selected={
                                dayjs(formData.departureTime, "HH:mm:ss", true).isValid()
                                    ? dayjs(formData.departureTime, "HH:mm:ss").toDate()
                                    : null
                            }
                            onChange={(date) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    departureTime: date ? dayjs(date).format("HH:mm:ss") : "",
                                }))
                            }
                            showTimeSelect
                            showTimeSelectOnly
                            timeFormat="HH:mm:ss"
                            timeIntervals={15}
                            dateFormat="HH:mm:ss"
                            className={`${inputStyle}`}
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">{t("arrival")}</label>
                        <DatePicker
                            selected={
                                dayjs(formData.arriveTime, "HH:mm:ss", true).isValid()
                                    ? dayjs(formData.arriveTime, "HH:mm:ss").toDate()
                                    : null
                            }
                            onChange={(date) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    arriveTime: date ? dayjs(date).format("HH:mm:ss") : "",
                                }))
                            }
                            showTimeSelect
                            showTimeSelectOnly
                            timeFormat="HH:mm:ss"
                            timeIntervals={15}
                            dateFormat="HH:mm:ss"
                            className={`${inputStyle}`}
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                            {t("flight-type")}
                        </label>
                        <select
                            name="type"
                            value={formData.type}
                            onChange={handleChange}
                            className={`${inputStyle}`}>
                            <option value="" disabled></option>
                            <option value="One-Way">{t("one-way")}</option>
                            <option value="Round-Trip">{t("round-trip")}</option>
                        </select>
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                            {t("class")}
                        </label>
                        <select
                            name="classType"
                            value={formData.classType}
                            onChange={handleChange}
                            className={`${inputStyle}`}>
                            <option value="" disabled></option>
                            <option value="Economy">{t("economy")}</option>
                            <option value="Premium-economy">{t("premium-economy")}</option>
                            <option value="Business">{t("business")}</option>
                            <option value="First">{t("first-class")}</option>
                        </select>
                    </div>

                </div>


                <div>
                    <label htmlFor="image-upload" className="w-full h-40 border border-dashed rounded flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition cursor-pointer">
                        {imagePreview ? (
                            <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded" />
                        ) : (
                            <span className="text-gray-500">{t("click_to_select")}</span>
                        )}
                    </label>
                    <input id="image-upload" type="file" name="companyImage" accept="image/*" onChange={handleImageChange} className="hidden" />

                </div>

                <div className="flex justify-between pt-4">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-300 rounded">{t("cancel")}</button>
                    <button onClick={handleSubmit} className="px-4 py-2 bg-primary text-white rounded">
                        {initialData ? t("update") : t("create")}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FlightModal;
